import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { SectionHead, Highlight, CtaButton, SCROLL_MT } from './shared';

function FAQItem({ item, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white transition hover:border-neutral-300" data-testid={`landing-faq-item-${idx}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5" data-testid={`landing-faq-toggle-${idx}`} aria-expanded={open}>
        <span className="text-[15px] sm:text-base font-bold text-neutral-900 leading-snug">{item.q}</span>
        <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${open ? 'bg-slate-900 text-white rotate-180' : 'bg-neutral-100 text-neutral-500'}`}>
          <ChevronDown size={15} />
        </span>
      </button>
      <div className={`grid transition-all duration-300 ease-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="px-5 sm:px-6 pb-5 text-sm sm:text-[15px] text-neutral-600 leading-relaxed">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

export function Faq({ faq }) {
  const items = (faq.items || []).filter(i => i && i.q);
  if (!items.length) return null;
  return (
    <section id="faq" className={`${SCROLL_MT} py-12 sm:py-16 lg:py-20`} data-testid="landing-faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <SectionHead label={faq.label || 'Before you ask'} title={faq.title || 'Questions, answered'} className="mb-7 sm:mb-8" />
        <div className="space-y-2.5">
          {items.map((item, i) => <FAQItem key={i} item={item} idx={i} />)}
        </div>
      </div>
    </section>
  );
}

export function FinalCta({ finalCta, onCta, onSecondary }) {
  return (
    <section className="pb-14 sm:pb-20" data-testid="final-cta-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-[26px] text-center text-white px-6 sm:px-12 py-9 sm:py-12 shadow-[0_30px_70px_-30px_rgba(0,47,167,0.55)]"
          style={{ background: 'linear-gradient(135deg,#002FA7 0%,#0f172a 60%,#1e293b 100%)' }}>
          <span className="pointer-events-none absolute -top-16 -left-16 w-48 h-48 rounded-full bg-amber-400/20 blur-3xl" aria-hidden="true" />
          <span className="pointer-events-none absolute -bottom-20 -right-10 w-56 h-56 rounded-full bg-blue-400/20 blur-3xl" aria-hidden="true" />
          <h2 className="font-[Manrope] font-extrabold text-2xl sm:text-[2.35rem] tracking-[-0.025em] leading-[1.1] mb-3" data-testid="final-cta-title"><Highlight text={finalCta.title} dark /></h2>
          {finalCta.subtitle && <p className="text-[13.5px] sm:text-[15px] text-white/70 max-w-lg mx-auto mb-6 leading-relaxed">{finalCta.subtitle}</p>}
          <CtaButton label={finalCta.cta_paid || 'Get Instant Access'} onClick={() => onCta('landing_footer')} variant="amber" size="md" className="w-full sm:w-auto" testId="final-cta-paid" />
          {finalCta.cta_free && (
            <div className="mt-4">
              <button onClick={() => onSecondary('landing_footer')} className="text-[12.5px] text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white/70 transition" data-testid="final-cta-free">
                {finalCta.cta_free} →
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function StickyCta({ cfg, price, show, onCta }) {
  if (!cfg?.enabled || !show) return null;
  const text = cfg.text || price;
  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-[max(10px,env(safe-area-inset-bottom))] pt-2 animate-slide-up" data-testid="sticky-cta">
      <div className="mx-auto max-w-lg rounded-2xl bg-slate-900/95 backdrop-blur border border-white/10 shadow-[0_-10px_40px_-15px_rgba(15,23,42,0.6)] px-3.5 py-2.5 flex items-center justify-between gap-3">
        {text && <p className="text-[13px] font-bold text-white leading-tight min-w-0 truncate" data-testid="sticky-cta-text">{text}</p>}
        <CtaButton label={cfg.cta_label || 'Get Instant Access'} onClick={() => onCta('sticky_bar')} variant="amber" size="sm" className="shrink-0" testId="sticky-cta-btn" />
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-slate-900 text-neutral-400 py-14" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo-navbar-dark.png" alt="CELPIPMax" className="w-8 h-8" />
              <span className="font-[Manrope] font-extrabold text-white">CELPIPMax</span>
            </div>
            <p className="text-sm">Your fastest path to a CLB 9+ CELPIP score.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/pricing" className="hover:text-white transition">Pricing</Link></li>
              <li><Link to="/blog" className="hover:text-white transition">Blog</Link></li>
              <li><Link to="/crs-calculator" className="hover:text-white transition" data-testid="footer-crs-link">Free CRS Calculator</Link></li>
              <li><Link to="/contact" className="hover:text-white transition" data-testid="footer-contact-link">Contact Us</Link></li>
              <li><Link to="/winning-stories" className="hover:text-white transition" data-testid="footer-stories-link">Winning Stories</Link></li>
              <li><Link to="/signup" className="hover:text-white transition">Sign Up</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/terms" className="hover:text-white transition">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-white transition">Refund Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-10 pt-7 text-center text-sm">
          <p>&copy; {new Date().getFullYear()} CELPIPMax. All rights reserved.</p>
          <p className="mt-3 text-[11px] leading-relaxed text-neutral-500 max-w-3xl mx-auto" data-testid="footer-disclaimer-celpip">
            CELPIP® is a registered trademark of Prometric LLC, including Paragon and its other global subsidiaries and affiliates. CelpipMax is an independent test preparation platform and is not affiliated with, endorsed by, or approved by Prometric LLC, Paragon, or the official CELPIP program.
          </p>
          <p className="mt-2 text-[11px] leading-relaxed text-neutral-500 max-w-3xl mx-auto" data-testid="footer-disclaimer-platforms">
            This site is not part of the Facebook, Google, or YouTube websites and is not endorsed by Meta Platforms, Inc. or Google LLC in any way. FACEBOOK and INSTAGRAM are trademarks of Meta Platforms, Inc. GOOGLE and YOUTUBE are trademarks of Google LLC.
          </p>
        </div>
      </div>
    </footer>
  );
}
